import { safeReturnTarget } from "@etain/domain/auth-return-target";

const TENANT_HINT = "etain.return-tenant";
const MICROSOFT_RETURN_HINT = "etain.microsoft-return";
type ReturnData = { target: string; tenantId?: string };

// A short-lived navigation hint only. The API reauthorizes every tenant and target.
export function rememberMicrosoftReturn(data?: ReturnData): void {
  try {
    if (!data) sessionStorage.removeItem(MICROSOFT_RETURN_HINT);
    else
      sessionStorage.setItem(
        MICROSOFT_RETURN_HINT,
        JSON.stringify({
          target: safeReturnTarget(data.target) ?? "/",
          ...(data.tenantId ? { tenantId: data.tenantId } : {}),
          expiresAt: Date.now() + 600_000,
        }),
      );
  } catch {
    /* Storage is optional; authentication never depends on this hint. */
  }
}

export function microsoftReturnHint(): ReturnData | undefined {
  try {
    const stored = JSON.parse(sessionStorage.getItem(MICROSOFT_RETURN_HINT) ?? "null") as {
      target?: unknown;
      tenantId?: unknown;
      expiresAt?: unknown;
    } | null;
    if (!stored || typeof stored.expiresAt !== "number" || stored.expiresAt <= Date.now()) return;
    const target = safeReturnTarget(stored.target);
    if (!target) return;
    return {
      target,
      ...(typeof stored.tenantId === "string" ? { tenantId: stored.tenantId } : {}),
    };
  } catch {
    return;
  }
}

export function microsoftPasswordFallback(): string {
  const hint = microsoftReturnHint();
  const search = new URLSearchParams({ sso: "failed" });
  if (hint) {
    search.set("returnTo", hint.target);
    if (hint.tenantId) search.set("tenantId", hint.tenantId);
  }
  return `/anmelden?${search}`;
}
export function rememberReturnTenant(tenantId?: string): void {
  try {
    if (tenantId) sessionStorage.setItem(TENANT_HINT, tenantId);
    else sessionStorage.removeItem(TENANT_HINT);
  } catch {
    /* Storage is optional. Server authorizes every hint. */
  }
}
export function previousReturnTenant(): string | undefined {
  try {
    return sessionStorage.getItem(TENANT_HINT) ?? undefined;
  } catch {
    return undefined;
  }
}
export function loginReturnData(location: { search: string; state: unknown }): {
  target: string;
  tenantId?: string;
} {
  const params = new URLSearchParams(location.search);
  const state = location.state as {
    from?: { pathname?: unknown; search?: unknown; hash?: unknown };
    tenantId?: unknown;
  } | null;
  const from = state?.from;
  const microsoftReturn = ["disabled", "unlinked", "tenant", "failed"].includes(
    params.get("sso") ?? "",
  )
    ? microsoftReturnHint()
    : undefined;
  const raw =
    params.get("returnTo") ??
    (typeof from?.pathname === "string"
      ? from.pathname +
        (typeof from.search === "string" ? from.search : "") +
        (typeof from.hash === "string" ? from.hash : "")
      : (microsoftReturn?.target ?? "/"));
  const target = safeReturnTarget(raw) ?? "/";
  const targetTenantId = new URL(target, "https://hub.invalid").searchParams.get("tenantId");
  const tenantId =
    params.get("tenantId") ??
    (typeof state?.tenantId === "string" ? state.tenantId : undefined) ??
    targetTenantId ??
    microsoftReturn?.tenantId;
  return { target, ...(tenantId ? { tenantId } : {}) };
}
